import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
type LegalDoc = 'privacy' | 'terms' | null;
interface Section {
  heading: string;
  body: string;
}
const PRIVACY: {
  title: string;
  sections: Section[];
} = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que recopilamos',
    body: 'Al realizar una compra recopilamos la información estrictamente necesaria para procesar tu pedido y entregarte el producto digital, como tu nombre y tu correo electrónico. No recopilamos información migratoria, documentos legales ni datos sensibles de tu caso.'
  }, {
    heading: '2. Cómo usamos tu información',
    body: 'Utilizamos tu correo electrónico únicamente para enviarte el acceso a la guía Visa-Prep y los materiales descargables incluidos en tu compra. No vendemos, alquilamos ni compartimos tu información con terceros con fines publicitarios.'
  }, {
    heading: '3. Procesamiento de pagos',
    body: 'Los pagos se procesan a través de plataformas externas seguras y encriptadas. Nosotros no almacenamos ni tenemos acceso a los datos completos de tu tarjeta de crédito o débito.'
  }, {
    heading: '4. Seguridad de los datos',
    body: 'Aplicamos medidas razonables para proteger tu información. Sin embargo, ningún método de transmisión por internet es 100% seguro, por lo que no podemos garantizar su seguridad absoluta.'
  }, {
    heading: '5. Cambios a esta política',
    body: 'Podemos actualizar esta Política de Privacidad en cualquier momento. La versión vigente será siempre la publicada en esta página.'
  }]
};
const TERMS: {
  title: string;
  sections: Section[];
} = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Naturaleza del producto',
    body: 'Visa-Prep es una guía educativa de carácter informativo. NO constituye asesoría legal y no reemplaza la consulta con un abogado de inmigración licenciado. No existe relación abogado-cliente derivada de la compra o el uso de esta guía.'
  }, {
    heading: '2. Sin garantía de resultados',
    body: 'Ningún producto, persona o entidad puede garantizar la aprobación de un beneficio migratorio. La decisión final sobre cualquier solicitud corresponde exclusivamente a USCIS u otra autoridad competente. El uso de esta guía no garantiza la aprobación de tu caso.'
  }, {
    heading: '3. Licencia de uso',
    body: 'Al comprar obtienes una licencia personal e intransferible para usar los materiales. Queda prohibida la reventa, redistribución, copia o difusión pública del contenido sin autorización por escrito.'
  }, {
    heading: '4. Producto digital y reembolsos',
    body: 'Debido a la naturaleza digital y de acceso inmediato del producto, las ventas son finales. Cualquier excepción quedará sujeta a la política del procesador de pagos utilizado.'
  }, {
    heading: '5. Limitación de responsabilidad',
    body: 'No nos hacemos responsables de decisiones tomadas con base en la información de la guía. Cada usuario es responsable de verificar los requisitos vigentes de USCIS y de la exactitud de su propio expediente.'
  }]
};
export function LegalFooter() {
  const [openDoc, setOpenDoc] = useState<LegalDoc>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDoc(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const content = openDoc === 'privacy' ? PRIVACY : openDoc === 'terms' ? TERMS : null;
  return <>
      <footer className="bg-slate-950 text-slate-500 py-10 text-center text-sm px-4" data-id="element-158">
        <p className="font-semibold text-slate-300" data-id="element-159">
          © {new Date().getFullYear()} Visa-Prep. Todos los derechos
          reservados.
        </p>
        <p className="mt-3 max-w-2xl mx-auto text-xs" data-id="element-160">
          Descargo de responsabilidad: Visa-Prep es una guía educativa y no
          constituye asesoría legal. El uso de esta guía no garantiza la
          aprobación de ningún beneficio migratorio.
        </p>
        <nav className="mt-5 flex items-center justify-center gap-6" data-id="element-161">
          <button type="button" onClick={() => setOpenDoc('privacy')} className="text-slate-400 hover:text-yellow-400 underline underline-offset-4 transition-colors" data-id="element-162">
            Privacidad
          </button>
          <span className="text-slate-700" data-id="element-163">|</span>
          <button type="button" onClick={() => setOpenDoc('terms')} className="text-slate-400 hover:text-yellow-400 underline underline-offset-4 transition-colors" data-id="element-164">
            Términos
          </button>
        </nav>
      </footer>

      {content && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={content.title} data-id="element-165">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setOpenDoc(null)} data-id="element-166" />

          {/* Panel */}
          <div className="relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-167">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0" data-id="element-168">
              <h2 className="text-xl font-bold text-slate-900" data-id="element-169">
                {content.title}
              </h2>
              <button type="button" onClick={() => setOpenDoc(null)} aria-label="Cerrar" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-id="element-170">
                <X size={20} data-id="element-171" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-6 space-y-6" data-id="element-172">
              {content.sections.map(section => <div key={section.heading} data-id="element-173">
                  <h3 className="font-bold text-slate-900 mb-2" data-id="element-174">
                    {section.heading}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-175">
                    {section.body}
                  </p>
                </div>)}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 shrink-0" data-id="element-176">
              <button type="button" onClick={() => setOpenDoc(null)} className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 rounded-xl transition-colors" data-id="element-177">
                Entendido
              </button>
            </div>
          </div>
        </div>}
    </>;
}